import type { ApiErrorBody } from '@/types/api';

export const API_BASE = '/api/v1';

/** Thrown for every non-2xx API response. Carries SQLSTATE when the server sent one. */
export class ApiError extends Error {
  readonly status: number;
  readonly sqlState: string | undefined;
  readonly retryAfterSeconds: number | undefined;

  constructor(
    status: number,
    message: string,
    sqlState?: string,
    retryAfterSeconds?: number,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.sqlState = sqlState;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

type TokenProvider = () => string | null;
type UnauthorizedHandler = () => void;

let tokenProvider: TokenProvider = () => null;
let onUnauthorized: UnauthorizedHandler = () => undefined;

/**
 * Wired once by AuthProvider. Keeping the token behind a getter rather than a
 * module constant means silent renew swaps it without re-creating the client.
 */
export function configureApiAuth(provider: TokenProvider, unauthorized: UnauthorizedHandler): void {
  tokenProvider = provider;
  onUnauthorized = unauthorized;
}

export function authHeaders(extra?: HeadersInit): Headers {
  const headers = new Headers(extra);
  const token = tokenProvider();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  return headers;
}

async function toApiError(response: Response): Promise<ApiError> {
  let message = `${response.status} ${response.statusText}`;
  let sqlState: string | undefined;
  let retryAfter: number | undefined;

  const retryHeader = response.headers.get('Retry-After');
  if (retryHeader) {
    const parsed = Number.parseInt(retryHeader, 10);
    if (Number.isFinite(parsed)) retryAfter = parsed;
  }

  try {
    const text = await response.text();
    if (text) {
      const body = JSON.parse(text) as ApiErrorBody;
      if (typeof body.message === 'string' && body.message) message = body.message;
      if (typeof body.sqlState === 'string') sqlState = body.sqlState;
      if (typeof body.retryAfterSeconds === 'number') retryAfter = body.retryAfterSeconds;
    }
  } catch {
    /* Non-JSON error body -- keep the status line. */
  }

  return new ApiError(response.status, message, sqlState, retryAfter);
}

/**
 * Fetches an already-complete same-origin path with the bearer token attached.
 *
 * Needed for URLs the server hands us verbatim -- e.g. an export's
 * `downloadUrl` -- which are relative and therefore same-origin, so the normal
 * auth header applies. They must not be treated as external links.
 */
export async function apiFetchUrl(url: string, init: RequestInit = {}): Promise<Response> {
  const headers = authHeaders(init.headers);
  const response = await fetch(url, { ...init, headers });

  if (response.status === 401) {
    onUnauthorized();
    throw new ApiError(401, 'Session expired. Sign in again to continue.', '28000');
  }

  if (!response.ok) throw await toApiError(response);
  return response;
}

export function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  return apiFetchUrl(`${API_BASE}${path}`, init);
}

export async function apiJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = authHeaders(init.headers);
  if (init.body !== undefined && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  const response = await apiFetch(path, { ...init, headers });
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}
